/**
 * The model picker: `/setup`'s model (by generation, slice 1b spec §4.4) and
 * Settings' routing (by provider, then generation, slice 1c spec §6.1). A
 * button with the model, its provider's mark and who serves it, that opens a
 * searchable list: a popover on a desktop, a sheet from the bottom on a
 * phone (Paper M2, M2b), on Base UI's Combobox. Each model shows its
 * provider's mark and can be tagged "Sees images" or "Default". It offers
 * only what it's given: Settings passes the connected providers' models.
 * No prices.
 */
import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSearch,
  ComboboxTrigger,
} from "@crew/ui/components/combobox";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "@crew/ui/components/drawer";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { ChevronDown, Search, X } from "lucide-react";
import { useId, useState, type ReactNode } from "react";
import type { AiProvider } from "../../app/me";
import type { CatalogModel } from "./onboarding";
import { ProviderLogo } from "./provider-tiles";

/** A heading and its models, and the provider whose mark they show. */
export type PickerGroup = {
  label: string;
  models: readonly CatalogModel[];
  mark?: AiProvider;
};

/** A group as Combobox takes it; `rule` draws a line above it where the provider changes. */
type ListGroup = { value: string; items: CatalogModel[]; mark?: AiProvider; rule: boolean };

function Tag({ tone, children }: { tone: "moss" | "muted"; children: ReactNode }) {
  return (
    <span
      // Out of the option's name: "GPT-5.6 Luna" stays what it's called.
      aria-hidden
      className={cn(
        "flex h-4.5 shrink-0 items-center rounded-sm px-1.5 text-[11px] leading-3.5 font-medium",
        tone === "moss" ? "bg-moss/10 text-moss" : "bg-muted text-muted-foreground",
      )}
    >
      {children}
    </span>
  );
}

/** A provider's mark, small: beside each model and in the button. */
function Mark({ provider }: { provider?: AiProvider }) {
  return provider ? <ProviderLogo provider={provider} small /> : null;
}

/** The groups as Combobox takes them, with the current model kept when nothing offers it. */
function listGroups(groups: readonly PickerGroup[], current: CatalogModel | null): ListGroup[] {
  const list = groups.map((g, i) => ({
    value: g.label,
    items: [...g.models],
    mark: g.mark,
    rule: i > 0 && g.mark !== groups[i - 1].mark,
  }));
  const offered = list.some((g) => g.items.some((m) => m.id === current?.id));
  if (!current || offered) return list;
  return [...list, { value: "Current", items: [current], rule: list.length > 0 }];
}

type Shared = {
  groups: ListGroup[];
  value: CatalogModel | null;
  onPick: (model: CatalogModel) => void;
  tagImages: boolean;
  recommend: boolean;
};

/** A model in the list: its provider's mark, its name and tags, and a check when it's picked. */
function ModelRow({
  model,
  mark,
  tagImages,
  recommend,
}: {
  model: CatalogModel;
  mark?: AiProvider;
} & Pick<Shared, "tagImages" | "recommend">) {
  return (
    <ComboboxItem
      value={model}
      data-model={model.id}
      // The primitive's check sits in the right-hand column the padding keeps.
      className="items-start gap-2.5 rounded-md py-2 pr-9 pl-2.5 data-selected:bg-accent [&>span:last-child:not(:first-child)]:top-2.5"
    >
      <Mark provider={mark} />
      <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-sm leading-5 font-medium text-foreground">{model.label}</span>
        {recommend && model.default ? <Tag tone="moss">Default</Tag> : null}
        {tagImages && model.image ? <Tag tone="muted">Sees images</Tag> : null}
      </span>
    </ComboboxItem>
  );
}

/** The search, then the models under their headings. */
function PickerBody({
  tagImages,
  recommend,
  sheet,
}: Pick<Shared, "tagImages" | "recommend"> & { sheet: boolean }) {
  return (
    <>
      <div
        className={cn(
          "flex h-9 shrink-0 items-center gap-2 px-2.5",
          sheet ? "mx-3 rounded-lg bg-muted" : "border-b border-border",
        )}
      >
        <Search className="size-3.75 shrink-0 text-hint" />
        <ComboboxSearch
          placeholder="Search models"
          aria-label="Search models"
          className="h-9 text-sm placeholder:text-hint any-pointer-coarse:text-base"
        />
      </div>
      {/* In the popover its frame shows it; inline in the sheet, the list's own empty state does. */}
      <ComboboxEmpty className="py-6 group-has-[[data-slot=combobox-list][data-empty]]/sheet:flex">
        No models match.
      </ComboboxEmpty>
      <ComboboxList
        className={cn(
          "p-0 pt-1.5",
          sheet ? "max-h-none px-3 pb-4" : "max-h-[min(26rem,var(--available-height))]",
        )}
      >
        {(group: ListGroup) => (
          <ComboboxGroup
            key={group.value}
            items={group.items}
            className={cn("pt-1.5", group.rule && "mt-1.5 border-t border-border")}
          >
            <ComboboxLabel className="px-2.5 pt-1.5 pb-1 text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
              {group.value}
            </ComboboxLabel>
            <ComboboxCollection>
              {(model: CatalogModel) => (
                <ModelRow
                  key={model.id}
                  model={model}
                  mark={group.mark}
                  tagImages={tagImages}
                  recommend={recommend}
                />
              )}
            </ComboboxCollection>
          </ComboboxGroup>
        )}
      </ComboboxList>
    </>
  );
}

const trigger =
  "flex h-10 w-full items-center gap-2.5 rounded-md bg-transparent pr-3 pl-2.5 text-left shadow-[0_0_0_1px_rgb(31_42_68/0.14)] transition-colors outline-none hover:bg-foreground/2 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50";

type Face = {
  value: CatalogModel | null;
  mark?: AiProvider;
  placeholder?: string;
  recommend: boolean;
  /** Who serves the model, in the button: "via Claude". */
  via?: ReactNode;
};

/** The button: the model picked (or the placeholder), its mark, and who serves it. */
function TriggerFace({ value, mark, placeholder, recommend, via }: Face) {
  return (
    <>
      {value ? <Mark provider={mark} /> : null}
      <span
        className={cn(
          "min-w-0 flex-1 shrink-0 basis-auto truncate text-[15px] leading-5",
          value ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {value?.label ?? placeholder}
      </span>
      {recommend && value?.default ? <Tag tone="moss">Default</Tag> : null}
      {/* The name comes first: "via …" gives way when the button is narrow. */}
      {via ? (
        <span className="min-w-0 shrink truncate text-meta text-muted-foreground">{via}</span>
      ) : null}
    </>
  );
}

type Props = Shared &
  Omit<Face, "value" | "recommend"> & {
    id?: string;
    label?: string;
    hint?: string;
    disabled: boolean;
    className?: string;
  };

/** On a phone: the button opens a sheet with the list in it, always open. */
function SheetPicker(props: Props) {
  const [open, setOpen] = useState(false);
  const { value, onPick, groups, label, hint, disabled } = props;
  return (
    <>
      <button
        type="button"
        id={props.id}
        // The same control as the popover's, whose list is in a sheet.
        role="combobox"
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen(true)}
        className={cn(trigger, props.className)}
      >
        <TriggerFace {...props} />
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </button>
      <Drawer open={open} onOpenChange={setOpen} showSwipeHandle>
        <DrawerContent className="data-[swipe-direction=down]:rounded-t-2xl">
          <div className="flex items-start gap-3 px-4 pt-1 pb-3">
            <div className="flex min-w-0 flex-1 flex-col">
              <DrawerTitle className="text-[17px] leading-6 font-medium">
                {label ?? "Choose a model"}
              </DrawerTitle>
              {hint ? (
                <DrawerDescription className="text-meta text-muted-foreground">
                  {hint}
                </DrawerDescription>
              ) : null}
            </div>
            <DrawerClose
              aria-label="Close"
              className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-foreground"
            >
              <X className="size-4" />
            </DrawerClose>
          </div>
          <div className="group/sheet flex max-h-[75dvh] flex-col overflow-y-auto">
            <Combobox
              inline
              open
              items={groups}
              value={value}
              itemToStringLabel={(m: CatalogModel) => m.label}
              isItemEqualToValue={(a: CatalogModel, b: CatalogModel) => a.id === b.id}
              onValueChange={(m: CatalogModel | null) => {
                if (!m) return;
                onPick(m);
                setOpen(false);
              }}
            >
              <PickerBody {...props} sheet />
            </Combobox>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

/** On a desktop: the button opens a popover under it. */
function PopoverPicker(props: Props) {
  const { value, onPick, groups, label, disabled } = props;
  const id = useId();
  return (
    <Combobox
      items={groups}
      value={value}
      disabled={disabled}
      itemToStringLabel={(m: CatalogModel) => m.label}
      isItemEqualToValue={(a: CatalogModel, b: CatalogModel) => a.id === b.id}
      onValueChange={(m: CatalogModel | null) => m && onPick(m)}
      // Opens at the current model, not the top of the list.
      onOpenChangeComplete={(open) => {
        if (open)
          document
            .querySelector(`[data-picker="${id}"] [data-selected]`)
            ?.scrollIntoView({ block: "nearest" });
      }}
    >
      <ComboboxTrigger id={props.id} aria-label={label} className={cn(trigger, props.className)}>
        <TriggerFace {...props} />
      </ComboboxTrigger>
      <ComboboxContent
        aria-label={label}
        data-picker={id}
        align="end"
        className="w-100 max-w-(--available-width) min-w-(--anchor-width) rounded-lg p-1.5 shadow-[0_0_0_1px_rgb(31_42_68/0.08),0_12px_32px_-8px_rgb(31_42_68/0.12)] ring-0"
        sideOffset={6}
      >
        <PickerBody {...props} sheet={false} />
      </ComboboxContent>
    </Combobox>
  );
}

export function ModelPicker({
  groups,
  value,
  onChange,
  id,
  label,
  hint,
  placeholder,
  via,
  disabled = false,
  recommend = false,
  tagImages = false,
  currentLabel,
  className,
}: {
  groups: readonly PickerGroup[];
  /** The model's id, or null for none. */
  value: string | null;
  onChange: (model: string) => void;
  id?: string;
  /** Its name for assistive tech, and the sheet's title. */
  label?: string;
  /** The sheet's line under its title: "Every ask starts here." */
  hint?: string;
  placeholder?: string;
  /** Who serves the model, shown in the button. */
  via?: ReactNode;
  disabled?: boolean;
  /** Tags each provider's default "Default". */
  recommend?: boolean;
  /** Tags each model that takes images "Sees images". */
  tagImages?: boolean;
  /** How the current model reads when no group offers it ("GLM 5 (no longer offered)"). */
  currentLabel?: string;
  className?: string;
}) {
  const mobile = useIsMobile();
  const offering = groups.find((g) => g.models.some((m) => m.id === value));
  const kept: CatalogModel | null = value
    ? { id: value, label: currentLabel ?? value, generation: "current", default: false }
    : null;
  const picked = offering?.models.find((m) => m.id === value) ?? kept;
  const props: Props = {
    groups: listGroups(groups, picked),
    value: picked,
    onPick: (m) => onChange(m.id),
    tagImages,
    recommend,
    mark: offering?.mark,
    via,
    placeholder,
    id,
    label,
    hint,
    disabled,
    className,
  };
  return mobile ? <SheetPicker {...props} /> : <PopoverPicker {...props} />;
}
